import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

import { Auth, retrievePublicToken } from "@utils/auth/Auth"; // retrievePublicToken now accepts an AbortSignal
import { ContextProvider } from "./contextProvider/ContextProvider";

import type { TokenData } from "@utils/auth/TokenStorage";

/**
 * This component is used to protect routes that are public
 * and don't require authentication.
 *
 * Uses a dedicated public-user token endpoint that exists in both dev
 * and production environments (resolved via SERVICE_HOST). The token is
 * stored in an isolated slot so it never overwrites an authenticated
 * user's session token.
 */
const PublicRoute = (): React.JSX.Element | null => {
    const [tokenData, setTokenData] = useState<TokenData | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        const fetchPublicToken = async (): Promise<void> => {
            try {
                const token = await retrievePublicToken(controller.signal);
                if (!controller.signal.aborted) {
                    setTokenData(token);
                }
            } catch (error) {
                console.error("Failed to retrieve public token:", error);
            }
        };

        void fetchPublicToken();

        // Cleanup function to abort the request on unmount
        return (): void => controller.abort();
    }, []);

    // While the public token is being fetched, render a fallback.
    if (!tokenData) {
        return null;
    }

    // By passing the token data as an initial state to the provider,
    // we ensure that all child components within the Outlet can access
    // the token from their very first render. This eliminates the TokenBridge
    // and prevents the race condition.
    // NOTE: This requires `ContextProvider` to accept an `initialState` prop.
    return (
        <ContextProvider initialState={{ token: tokenData.token, username: tokenData.username }}>
            <Outlet />
            <Auth isPublic />
        </ContextProvider>
    );
};

export default PublicRoute;
